import { useEffect } from 'react';
import { Check, Zap, Crown, Building, AlertCircle, Loader2 } from 'lucide-react';
import { useBillingPlans } from '@/modules/billing/ui/hooks/useBilling';
import type { WizardFormData } from '../types';

interface PlanSelectionStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  onValidChange: (isValid: boolean) => void;
  errors: Record<string, string>;
}

const PLAN_ICONS: Record<string, typeof Zap> = {
  free: Building,
  starter: Zap,
  professional: Crown,
  enterprise: Building,
};

function getPlanIcon(planName: string) {
  const normalized = planName.toLowerCase();
  return PLAN_ICONS[normalized] || Zap;
}

function formatPrice(price: number | string, billingCycle: string): string {
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (!num || isNaN(num)) return 'Free';
  return `$${num.toFixed(0)}/${billingCycle === 'YEARLY' ? 'year' : 'mo'}`;
}

export function PlanSelectionStep({ formData, onChange, onValidChange }: PlanSelectionStepProps) {
  const { plans, isLoading } = useBillingPlans();

  useEffect(() => {
    onValidChange(!!formData.planId);
  }, [formData.planId, onValidChange]);

  // Auto-select popular plan if none selected and plans loaded (sortOrder 2 is typically "popular")
  useEffect(() => {
    if (!formData.planId && plans.length > 0) {
      const popular = plans.find((p) => p.sortOrder === 2);
      if (popular) {
        onChange({ planId: popular.id });
      }
    }
  }, [plans, formData.planId, onChange]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-4" />
        <p className="text-theme-secondary">Loading plans...</p>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-lg font-semibold text-theme mb-2">No Plans Available</h3>
        <p className="text-sm text-theme-secondary text-center max-w-md">
          There are no billing plans configured yet. Please go to the{' '}
          <span className="font-medium text-indigo-600 dark:text-indigo-400">Billing</span> section
          and create at least one plan before creating a tenant.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-lg font-semibold text-theme">Select a Plan</h3>
        <p className="text-sm text-theme-secondary mt-1">Choose the plan that best fits this tenant's needs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((plan) => {
          const Icon = getPlanIcon(plan.name);
          const isSelected = formData.planId === plan.id;
          const isPopular = plan.sortOrder === 2;
          const enabledFeatures = plan.features
            ? Object.entries(plan.features).filter(([, v]) => v).map(([k]) => k)
            : [];

          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onChange({ planId: plan.id })}
              className={`relative p-6 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700'
              }`}
            >
              {isPopular && (
                <span className="absolute -top-3 left-4 px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded-full">
                  Popular
                </span>
              )}

              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-theme">{plan.name}</h4>
                    {plan.description && (
                      <p className="text-xs text-theme-tertiary">{plan.description}</p>
                    )}
                  </div>
                </div>

                {isSelected && (
                  <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              <div className="mt-4">
                <span className="text-2xl font-bold text-theme">
                  {formatPrice(plan.basePrice, plan.billingCycle)}
                </span>
              </div>

              {enabledFeatures.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {enabledFeatures.slice(0, 4).map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-theme-secondary">
                      <Check className="w-4 h-4 text-green-500 shrink-0" />
                      {feature.replace(/_/g, ' ')}
                    </li>
                  ))}
                  {enabledFeatures.length > 4 && (
                    <li className="text-xs text-theme-tertiary">
                      +{enabledFeatures.length - 4} more features
                    </li>
                  )}
                </ul>
              )}

              {/* Limits info */}
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="flex justify-between text-xs text-theme-tertiary">
                  <span>
                    Up to {plan.includedUnits === -1 ? 'unlimited' : plan.includedUnits} units
                  </span>
                  <span>
                    {plan.includedUsers === -1 ? 'Unlimited' : plan.includedUsers} users
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
